/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo } from "react";
import { IInspectorControl_Responsive, TInspectorControlProps } from "../InspectorControlTypes";
import {
	defaultResolutionId,
	IControlResponsiveValue_EditorMetaData
} from "../../mappers/responsiveValueMapper";
import { FieldGroup, FieldResponsiveButton } from "@adapptio/lib-editor-ui";
import {
	getPreviewResolutionById,
	isMediaResolutionId,
	usePreviewResolutionContext
} from "../../../previewResolution";

export interface IInspectorControlResponsiveProps
	extends TInspectorControlProps<IInspectorControl_Responsive> {}

export const InspectorControlResponsive: React.FC<IInspectorControlResponsiveProps> = ({ model }) => {
	const previewResCtx = usePreviewResolutionContext();

	const expanded =
		!!model.schema.getEditorMetaData<IControlResponsiveValue_EditorMetaData>(model)?.showExpanded;

	const toggleShowExpanded = useCallback(() => {
		if (!model) {
			return;
		}

		const prevMeta = model.schema.getEditorMetaData<IControlResponsiveValue_EditorMetaData>(model) || {};
		model.schema.setEditorMetaData<IControlResponsiveValue_EditorMetaData>(model, {
			...prevMeta,
			showExpanded: !prevMeta.showExpanded
		});
	}, [ model ]);

	useEffect(() => {
		const resolutionId = previewResCtx?.mediaResolutionId;

		const mainResolutionId = isMediaResolutionId(resolutionId) ? resolutionId : defaultResolutionId;

		const prevMeta = model.schema.getEditorMetaData<IControlResponsiveValue_EditorMetaData>(model) || {};

		model.schema.setEditorMetaData<IControlResponsiveValue_EditorMetaData>(model, {
			...prevMeta,
			mainResolutionId
		});
	}, [ previewResCtx, previewResCtx?.mediaResolutionId ]);

	const previewResolution = useMemo(
		() => getPreviewResolutionById(previewResCtx?.mediaResolutionId),
		[ previewResCtx?.mediaResolutionId ]
	);

	return (
		<FieldGroup preventTruncate>
			<FieldResponsiveButton
				onClick={toggleShowExpanded}
				selected={expanded}
				iconName={previewResolution?.icon}
				tooltip={previewResolution?.label}
			/>
		</FieldGroup>
	);
};
